/**
 * @file WithEAIOverview.jsx
 * @description withEAI 시스템 통합 미들웨어 소개 페이지입니다.
 * Hero(허브 다이어그램) → 네 가지 핵심 능력 → CTA 순서로 구성됩니다.
 */

import { ArrowRight } from 'lucide-react';
import { withEAI as withEAIImg } from '../../assets';

/* withEAI 허브에 연결된 외부 시스템 노드 — 각도는 12시 기준 시계방향 */
const HUB_NODES = [
  { id: 'erp', label: 'ERP', sub: 'SAP / Oracle', angle: 30,  color: '#2c4a7a' },
  { id: 'bi',  label: 'BI',  sub: 'Analytics',    angle: 90,  color: '#2c4a7a' },
  { id: 'mes', label: 'MES', sub: '제조실행',       angle: 150, color: '#1f6b4f' },
  { id: 'wms', label: 'WMS', sub: '창고관리',       angle: 210, color: '#1f6b4f' },
  { id: 'rws', label: 'RWS', sub: '칭량관리',       angle: 270, color: '#c98a2b' },
  { id: 'qc',  label: 'QC',  sub: 'LIMS · 품질',   angle: 330, color: '#2c4a7a' },
];

/* [비즈니스] EAI 허브가 갖춰야 할 4가지 핵심 역할 — 카드 단위 노출을 위해
   디자인 메타(코드/영문명/어센트)와 기술 태그·핵심 기능 키워드를 함께 보관합니다.
   accent 색은 EAI 페이지 팔레트(허브 다이어그램 노드 색)에서 가져와 색감 일관성을 유지합니다. */
const CAPABILITIES = [
  {
    num: '01',
    en: 'Protocol Conversion',
    title: '프로토콜 변환',
    accent: '#b54a1f',
    short: 'SAP의 RFC·IDoc, 웹 솔루션의 REST/JSON, 레거시의 SOAP·DB Link 등 이질적인 통신 방식을 중간에서 변환하여 시스템 간 자유로운 대화를 만듭니다.',
    tags: ['RFC', 'IDoc', 'REST', 'SOAP'],
    keywords: ['양방향 어댑터', '표준 메시지 정규화', '동기·비동기 라우팅', '레거시 호환 보장'],
  },
  {
    num: '02',
    en: 'Data Mapping',
    title: '데이터 매핑',
    accent: '#c98a2b',
    short: 'ERP의 마스터 코드와 MES·WMS 운영 코드 간 양방향 매핑을 자동화. 품목·고객사·BOM이 한 번 정의되면 모든 시스템에서 동일하게 통용됩니다.',
    tags: ['JSON', 'XML', 'CSV', 'EDI'],
    keywords: ['마스터 코드 매핑', '양방향 변환 규칙', '품목·BOM 통일', '자동 동기화'],
  },
  {
    num: '03',
    en: 'Queue Management',
    title: '큐 관리 / 버퍼링',
    accent: '#2c4a7a',
    short: 'ERP 점검·네트워크 단절 등 장애 상황에서도 EAI가 메시지를 보관, 복구 시 순차 전달. 공장 시스템 중단을 차단하여 업무 연속성을 보장합니다.',
    tags: ['Async', 'Pub/Sub', 'Store-Forward'],
    keywords: ['메시지 영속화', '순차 보장 전송', '자동 재시도', '장애 복구 연속성'],
  },
  {
    num: '04',
    en: 'Monitoring',
    title: '모니터링 / 추적성',
    accent: '#1f6b4f',
    short: '어떤 데이터가 언제, 어디로 전달되었는지 단일 콘솔에서 확인. 인터페이스 성공·실패 이력과 로그를 시각화하여 운영팀의 즉각 대응을 지원합니다.',
    tags: ['Dashboard', 'Audit Trail', 'Alert'],
    keywords: ['실시간 흐름 추적', '인터페이스 성공·실패 이력', '로그 시각화', '운영 알림 자동화'],
  },
];

/* ──────────────────────────────────────
   허브 SVG 다이어그램
   6개 외부 시스템과 중앙 withEAI 허브를 점선 흐름 + 패킷 애니메이션으로 연결합니다.
   ────────────────────────────────────── */
function EAIHubDiagram() {
  const cx = 200, cy = 200;
  const orbitR = 148;
  const nodeR  = 34;
  const centerR = 55;

  /** 12시 기준 시계방향 각도(deg)를 SVG (x, y) 좌표로 변환합니다. */
  const toPos = (deg, r) => ({
    x: cx + r * Math.sin((deg * Math.PI) / 180),
    y: cy - r * Math.cos((deg * Math.PI) / 180),
  });

  return (
    <svg
      viewBox="0 0 400 400"
      className="weai-hub-svg"
      aria-label="withEAI 시스템 허브 다이어그램"
    >
      <defs>
        {/* 각 노드 → 중앙 허브 방향 패킷 이동 경로 */}
        {HUB_NODES.map((node) => {
          const p = toPos(node.angle, orbitR);
          return (
            <path
              key={`pdef-${node.id}`}
              id={`weai-p-${node.id}`}
              d={`M ${p.x.toFixed(2)} ${p.y.toFixed(2)} L ${cx} ${cy}`}
            />
          );
        })}
      </defs>

      {/* 장식 외곽 원 */}
      <circle cx={cx} cy={cy} r={192} fill="none" stroke="#d8d0be" strokeWidth="0.5" strokeDasharray="3 9" />
      {/* 노드 궤도 원 */}
      <circle cx={cx} cy={cy} r={orbitR} fill="none" stroke="#bfb8a8" strokeWidth="1" />
      {/* 내부 보조 원 */}
      <circle cx={cx} cy={cy} r={105} fill="none" stroke="#d8d0be" strokeWidth="0.5" />

      {/* 중심-노드 점선 연결 (데이터 흐름 dashflow 애니메이션) */}
      {HUB_NODES.map((node, i) => {
        const p = toPos(node.angle, orbitR);
        return (
          <line
            key={`ln-${node.id}`}
            x1={p.x.toFixed(2)} y1={p.y.toFixed(2)}
            x2={cx} y2={cy}
            stroke="#bfb0a0"
            strokeWidth="1"
            strokeDasharray="5 8"
            style={{
              animation: 'weaiDashflow 2.8s linear infinite',
              animationDelay: `${-(i * 0.46).toFixed(2)}s`,
            }}
          />
        );
      })}

      {/* 데이터 패킷 — 노드에서 중앙 허브로 이동합니다 */}
      {HUB_NODES.map((node, i) => (
        <circle key={`pkt-${node.id}`} r="2.8" fill={node.color} opacity="0.8">
          <animateMotion
            dur="2.8s"
            repeatCount="indefinite"
            begin={`${-(i * 0.46).toFixed(2)}s`}
          >
            <mpath href={`#weai-p-${node.id}`} />
          </animateMotion>
        </circle>
      ))}

      {/* 시스템 노드 원 */}
      {HUB_NODES.map((node) => {
        const p = toPos(node.angle, orbitR);
        return (
          <g key={node.id}>
            <circle cx={p.x.toFixed(2)} cy={p.y.toFixed(2)} r={nodeR} fill="#fff" stroke={node.color} strokeWidth="1.5" />
            <text
              x={p.x.toFixed(2)} y={(p.y - 5).toFixed(2)}
              textAnchor="middle"
              fontSize="11" fontWeight="700" fill={node.color} fontFamily="inherit"
            >
              {node.label}
            </text>
            <text
              x={p.x.toFixed(2)} y={(p.y + 9).toFixed(2)}
              textAnchor="middle"
              fontSize="7.5" fill="#857e6e" fontFamily="inherit"
            >
              {node.sub}
            </text>
          </g>
        );
      })}

      {/* 물결 효과 — 중앙 허브에서 바깥으로 퍼져나가는 3단계 파동입니다 */}
      {[0, 1, 2].map((delay) => (
        <circle key={delay} cx={cx} cy={cy} fill="none" stroke="#b54a1f">
          <animate
            attributeName="r"
            from={centerR} to={centerR + 48}
            dur="3s" repeatCount="indefinite" begin={`${delay}s`}
            calcMode="spline" keyTimes="0;1" keySplines="0.15 0.5 0.4 1"
          />
          <animate
            attributeName="opacity"
            from="0.45" to="0"
            dur="3s" repeatCount="indefinite" begin={`${delay}s`}
            calcMode="spline" keyTimes="0;1" keySplines="0.15 0.5 0.4 1"
          />
          <animate
            attributeName="stroke-width"
            from="1.8" to="0.3"
            dur="3s" repeatCount="indefinite" begin={`${delay}s`}
          />
        </circle>
      ))}

      {/* 중앙 withEAI 허브 */}
      <circle cx={cx} cy={cy} r={centerR + 10} fill="none" stroke="#d5cebe" strokeWidth="0.8" strokeDasharray="2 6" />
      <circle cx={cx} cy={cy} r={centerR} fill="#fff" stroke="#b54a1f" strokeWidth="2" />
      <text
        x={cx} y={cy}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="17" fontWeight="700" fontStyle="italic"
        fontFamily="inherit"
      >
        <tspan fill="#1a1a1a">with</tspan><tspan fill="#b54a1f">EAI</tspan>
      </text>
    </svg>
  );
}

/* ══════════════════════════════════════
   섹션 1: 히어로 — 핵심 메시지 + 허브 다이어그램
   ══════════════════════════════════════ */
function WithEAIHero({ onNavigate }) {
  const specs = [
    { label: 'PROTOCOL', value: 'RFC · IDoc · REST · SOAP' },
    { label: 'FORMAT',   value: 'JSON · XML · CSV · EDI'   },
    { label: 'PATTERN',  value: 'Sync · Async · Pub/Sub'   },
  ];

  return (
    <section className="weai-hero">
      <div className="container">
        <div className="weai-hero-inner">
          {/* 다이어그램을 왼쪽 끝에 배치합니다 */}
          <div className="weai-hero-diagram">
            <EAIHubDiagram />
          </div>

          <div className="weai-hero-text">
            <p className="weai-eyebrow">Enterprise Application Integration</p>
            <h1 className="weai-hero-title">
              모든 시스템이 <em className="weai-accent">하나의 중심</em>을 향하도록.
            </h1>
            <p className="weai-hero-desc">
              <strong>withEAI</strong>는 ERP·MES·WMS·RWS·LIMS 등 기업 내 흩어진 시스템을 하나로
              연결하는 통합 미들웨어입니다. 프로토콜과 데이터 형식의 차이를 흡수하고, 장애 시에도
              데이터 흐름을 끊김 없이 이어갑니다.
            </p>
            <div className="weai-specs">
              {specs.map((sp) => (
                <div key={sp.label} className="weai-spec-row">
                  <span className="weai-spec-label">{sp.label}</span>
                  <span className="weai-spec-value">{sp.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════
   섹션 2: 네 가지 핵심 능력
   ══════════════════════════════════════ */
function WithEAICapabilities() {
  return (
    <section className="weai-caps">
      {/* [기술] 배경 도트 그리드 — 카드와 별개 레이어로 깔아 EAI 베이지 톤을 유지 */}
      <svg
        className="weai-caps-bg"
        aria-hidden="true"
        viewBox="0 0 1280 1000"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <pattern id="weai-cap-dot-grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="1" fill="rgba(181,74,31,0.12)" />
          </pattern>
        </defs>
        <rect width="1280" height="1000" fill="url(#weai-cap-dot-grid)" />
      </svg>

      <div className="container">
        <div className="weai-caps-head">
          <div className="weai-caps-section-head">
            <p className="weai-eyebrow">CORE CAPABILITIES</p>
            <h2 className="weai-caps-title">
              허브가 갖춰야 할 <em className="weai-accent">네 가지 능력</em>
            </h2>
          </div>
          <p className="weai-caps-head-desc">
            단순 메시지 패스스루를 넘어, EAI는 변환·매핑·완충·관측의 네 축을 갖출 때 비로소
            제 역할을 합니다. withEAI는 이 네 축을 모두 충족합니다.
          </p>
        </div>

        <div className="weai-caps-grid">
          {CAPABILITIES.map((cap) => (
            <article
              key={cap.num}
              className="weai-cap-card"
              style={{ '--card-accent': cap.accent }}
            >
              <div className="weai-cap-mark">
                <div className="weai-cap-mark-top">
                  <span className="weai-cap-code">{cap.num}</span>
                  <span className="weai-cap-en">{cap.en}</span>
                </div>
              </div>

              <div className="weai-cap-body">
                <div>
                  <h3 className="weai-cap-title">{cap.title}</h3>
                  <p className="weai-cap-desc">{cap.short}</p>
                </div>

                <div>
                  <div className="weai-cap-label">TECHNOLOGIES</div>
                  <div className="weai-cap-tags">
                    {cap.tags.map((t) => (
                      <span key={t} className="weai-cap-tag">{t}</span>
                    ))}
                  </div>
                </div>

                <div className="weai-cap-keys-wrap">
                  <div className="weai-cap-label">KEY FEATURES</div>
                  <ul className="weai-cap-keys">
                    {cap.keywords.map((k) => (
                      <li key={k} className="weai-cap-key">{k}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════
   섹션 3: CTA — 도입 문의 유도
   ══════════════════════════════════════ */
function WithEAICTA({ onNavigate }) {
  return (
    <section className="weai-cta">
      <div className="container">
        <div className="weai-cta-inner">
          {/* 왼쪽: 제목 + 설명 + 버튼 */}
          <div className="weai-cta-left">
            <p className="weai-eyebrow">CONTACT</p>
            <h2 className="weai-cta-title">
              단절된 시스템을<br />
              <em className="weai-accent">하나의 흐름</em>으로.
            </h2>
            <p className="weai-cta-desc">
              ㈜새힘정보기술이 21년간 축적한 GMP IT 노하우로 귀사의 통합 미들웨어를<br />
              책임지고 구축합니다. 도입 검토, 데모 요청, 견적 문의 모두 환영합니다.
            </p>
            <div className="weai-cta-actions">
              <button
                type="button"
                className="weai-btn weai-btn-primary"
                onClick={() => onNavigate('/contact')}
              >
                도입 상담 요청 <ArrowRight size={16} />
              </button>
            </div>
          </div>
          {/* 오른쪽: 실제 화면 이미지 */}
          <div className="weai-cta-right">
            <img src={withEAIImg} alt="withEAI 화면 예시" className="weai-cta-image" />
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * withEAI 전체 페이지 진입점.
 * SolutionPage에서 activeTab === 'witheai'일 때 렌더링됩니다.
 */
export default function WithEAIOverview({ onNavigate }) {
  return (
    <>
      <WithEAIHero onNavigate={onNavigate} />
      <WithEAICapabilities />
      <WithEAICTA onNavigate={onNavigate} />
    </>
  );
}
